import { MessageBubble, MessageFeed, MessageInput } from '@/components/chat';
import ErrorMessage from '@/components/errorMessage';
import { useApiContext } from '@/contexts/apiContext';
import LayersIcon from '@mui/icons-material/Layers';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { Box, Button, Typography } from '@mui/material';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { RightPanelContext } from '../entityContextPanel/types';
import { ChatHeader } from './chatHeader';
import { extractEntityFromResponse } from './entityExtraction';
import { QuickActionsBar } from './quickActionsBar';
import { AssistantChatHandle, ChatMessage, Props } from './types';
import { useQuickActions } from './useQuickActions';

// Re-export public types and helpers for consumers and backwards compatibility
export * from './entityExtraction';
export * from './types';

const AssistantChat = forwardRef<AssistantChatHandle, Props>(
  (
    {
      onEntityDetected,
      rightPanelContext,
      pipelineExecutionState,
      selectedStep,
      suppressEvents,
      onExecuteStep,
      initialConversationId,
      onConversationSaved,
      onConversationDeleted,
    },
    ref,
  ) => {
    const apiContext = useApiContext();

    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [conversationId, setConversationId] = useState<string | null>(null);
    const conversationIdRef = useRef<string | null>(null);

    const streamSourceRef = useRef<any>(null);
    const rightPanelContextRef = useRef<RightPanelContext | undefined>(rightPanelContext);

    useEffect(() => {
      rightPanelContextRef.current = rightPanelContext;
    }, [rightPanelContext]);

    const lastLoadedConvIdRef = useRef<string | null>(null);

    // Load conversation and past messages when initialConversationId changes
    useEffect(() => {
      if (initialConversationId && initialConversationId !== 'new') {
        if (lastLoadedConvIdRef.current === initialConversationId) {
          return;
        }
        lastLoadedConvIdRef.current = initialConversationId;
        conversationIdRef.current = initialConversationId;
        setConversationId(initialConversationId);

        let isCancelled = false;
        apiContext
          .getAssistantUserInteractions(initialConversationId)
          .then((interactions) => {
            if (isCancelled) return;
            const loadedMessages: ChatMessage[] = [];
            for (const ui of interactions || []) {
              if (ui.userMessage) {
                loadedMessages.push({
                  id: `user_${ui.id}`,
                  role: 'user',
                  content: ui.userMessage,
                  timestamp: new Date(ui.messageDatetime),
                });
              }
              if (ui.systemResponse || (ui.toolCalls && ui.toolCalls.length > 0)) {
                loadedMessages.push({
                  id: `asst_${ui.id}`,
                  role: 'system',
                  content: ui.systemResponse || '',
                  toolCalls: ui.toolCalls,
                  timestamp: new Date(ui.responseDatetime || ui.messageDatetime),
                });
              }
            }
            setMessages(loadedMessages);
          })
          .catch((err) => {
            console.error('Failed to load assistant interactions:', err);
          });

        return () => {
          isCancelled = true;
        };
      } else if (!initialConversationId || initialConversationId === 'new') {
        // If we already have an active conversation running, do NOT wipe messages
        if (conversationIdRef.current && conversationIdRef.current !== 'new') {
          return;
        }
        lastLoadedConvIdRef.current = null;
        conversationIdRef.current = null;
        setConversationId(null);
        setMessages([]);
      }
    }, [initialConversationId, apiContext]);

    const setConvId = useCallback((id: string | null) => {
      lastLoadedConvIdRef.current = id;
      conversationIdRef.current = id;
      setConversationId(id);
    }, []);

    const handleStartNewConversation = useCallback(() => {
      if (streamSourceRef.current) {
        streamSourceRef.current.close();
        streamSourceRef.current = null;
      }
      setConvId(null);
      setMessages([]);
      setIsProcessing(false);
      setError(null);
    }, [setConvId]);

    const handleClearChat = useCallback(async () => {
      if (streamSourceRef.current) {
        streamSourceRef.current.close();
        streamSourceRef.current = null;
      }
      const convIdToDelete = conversationIdRef.current;
      setConvId(null);
      setMessages([]);
      setIsProcessing(false);
      setError(null);

      if (convIdToDelete && convIdToDelete !== 'new') {
        try {
          await apiContext.deleteAssistantConversation(convIdToDelete);
          if (onConversationDeleted) {
            onConversationDeleted(convIdToDelete);
          }
        } catch (err) {
          console.error('Failed to delete assistant conversation from database:', err);
        }
      }
    }, [setConvId, apiContext, onConversationDeleted]);

    const handleSendMessage = useCallback(
      async (userMessageText: string) => {
        if (!userMessageText.trim() || isProcessing) return;

        const currentConvId = conversationIdRef.current || 'new';
        const userMsgId = `user_${Date.now()}`;
        const assistantMsgId = `asst_${Date.now()}`;
        const timestamp = new Date();

        setMessages((prev) => [
          ...prev,
          {
            id: userMsgId,
            role: 'user',
            content: userMessageText,
            timestamp,
          },
          {
            id: assistantMsgId,
            role: 'system',
            content: '',
            timestamp,
            isStreaming: true,
            status: 'Thinking...',
          },
        ]);

        setIsProcessing(true);
        setError(null);

        let accumulatedContent = '';
        const accumulatedToolCalls: any[] = [];

        const currentCtx = rightPanelContextRef.current;
        let entityContext: string | undefined;
        if (currentCtx && currentCtx.type !== 'empty') {
          if (currentCtx.type === 'pipeline') {
            entityContext = `Active Pipeline: "${currentCtx.pipelineName || currentCtx.pipelineId}" (ID: ${currentCtx.pipelineId})${currentCtx.selectedStepId ? `, Current Step: "${currentCtx.selectedStepId}"` : ''}`;
          } else if (currentCtx.type === 'single') {
            entityContext = `Viewing ${currentCtx.entityType}: "${currentCtx.entityName || currentCtx.entityId}" (ID: ${currentCtx.entityId})`;
          } else if (currentCtx.type === 'entity_list') {
            entityContext = `Viewing List of ${currentCtx.entityList?.items?.length ?? 0} ${currentCtx.entityList?.title || 'Components'}`;
          }
        }

        try {
          const source = await apiContext.streamAssistantMessage(
            currentConvId,
            userMessageText,
            pipelineExecutionState?.pipelineInstanceId || '',
            (eventName, payLoad) => {
              const { content, toolCalls, status: incomingStatus, conversationId: newConvId } = payLoad || {};
              if (newConvId) {
                setConvId(newConvId);
                if (onConversationSaved) {
                  onConversationSaved(newConvId);
                }
              }
              if (toolCalls && toolCalls.length > 0) {
                for (const incoming of toolCalls) {
                  const existingIndex = accumulatedToolCalls.findIndex(
                    (tc) =>
                      (incoming.id && tc.id === incoming.id) ||
                      (!incoming.id && tc.name === incoming.name && !tc.result),
                  );
                  if (existingIndex >= 0) {
                    accumulatedToolCalls[existingIndex] = { ...accumulatedToolCalls[existingIndex], ...incoming };
                  } else {
                    accumulatedToolCalls.push(incoming);
                  }
                }
              }
              if (eventName === 'message') {
                if (content) {
                  accumulatedContent += content;
                }

                const detectedEntities = extractEntityFromResponse(accumulatedToolCalls, accumulatedContent);
                // if (detectedEntities && detectedEntities.length > 0 && onEntityDetected) {
                //   const key = `${detectedEntities[0].type}_${detectedEntities[0].id || (detectedEntities[0].items ? detectedEntities[0].items.length : '')}`;
                //   if (key !== lastNotifiedEntityKey) {
                //     lastNotifiedEntityKey = key;
                //     onEntityDetected(detectedEntities[0]);
                //   }
                // }

                setMessages((prev) =>
                  prev.map((msg) => {
                    if (msg.id === assistantMsgId) {
                      let currentStatus = incomingStatus || msg.status;
                      const pendingTools = accumulatedToolCalls.filter((tc) => !tc.result);
                      if (pendingTools.length > 0) {
                        const toolNames = pendingTools.map((t) => t.name).join(', ');
                        currentStatus = `Running ${toolNames}...`;
                      } else if (accumulatedToolCalls.length > 0 && !accumulatedContent) {
                        currentStatus = 'Processing tool results...';
                      }

                      if (accumulatedContent.length > 0) {
                        // currentStatus = undefined;
                      }

                      return {
                        ...msg,
                        content: accumulatedContent,
                        toolCalls: [...accumulatedToolCalls],
                        status: currentStatus,
                        detectedEntities: detectedEntities || msg.detectedEntities,
                      };
                    }
                    return msg;
                  }),
                );
              } else if (eventName === 'error') {
                const errMsg = typeof payLoad === 'string' ? payLoad : payLoad?.message || 'Error from assistant';
                setError(errMsg);
                setIsProcessing(false);
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === assistantMsgId ? { ...msg, isStreaming: false, status: undefined } : msg,
                  ),
                );
              } else if (eventName === 'close') {
                const finalToolCalls = accumulatedToolCalls.map((tc) => ({
                  ...tc,
                  result: tc.result !== undefined ? tc.result : 'Completed',
                }));

                let finalContent = accumulatedContent || content || '';
                if (!finalContent && finalToolCalls.length > 0) {
                  finalContent =
                    'I retrieved the requested components. You can view, search, and inspect them in the panel on the right.';
                } else if (!finalContent) {
                  finalContent = 'Somethig went wrong. Please try again.';
                }

                const finalDetectedEntities = extractEntityFromResponse(finalToolCalls, finalContent);
                if (finalDetectedEntities && finalDetectedEntities.length > 0 && onEntityDetected) {
                  onEntityDetected(finalDetectedEntities[0]);
                }

                setMessages((prev) =>
                  prev.map((msg) => {
                    if (msg.id === assistantMsgId) {
                      return {
                        ...msg,
                        content: finalContent,
                        toolCalls: finalToolCalls,
                        isStreaming: false,
                        status: undefined,
                        detectedEntities: finalDetectedEntities || msg.detectedEntities,
                      };
                    }
                    return msg;
                  }),
                );
                setIsProcessing(false);
              }
            },
            entityContext,
          );
          streamSourceRef.current = source;
        } catch (err: any) {
          setError(err?.message || 'Failed to send message to assistant.');
          setIsProcessing(false);
          setMessages((prev) => prev.map((msg) => (msg.id === assistantMsgId ? { ...msg, isStreaming: false } : msg)));
        }
      },
      [apiContext, isProcessing, setConvId, onEntityDetected, onConversationSaved],
    );

    const { quickActions, handleQuickActionClick, executeDirectAction, activeActionLabel } = useQuickActions({
      rightPanelContext,
      pipelineExecutionState,
      selectedStep,
      suppressEvents,
      onExecuteStep,
      onEntityDetected,
      isProcessing,
      setIsProcessing,
      setError,
      setMessages,
      handleSendMessage,
      conversationIdRef,
      setConvId,
      onConversationSaved,
    });

    useImperativeHandle(
      ref,
      () => ({
        startNewConversation: handleStartNewConversation,
        sendPrompt: handleSendMessage,
        executeDirectAction,
      }),
      [handleStartNewConversation, handleSendMessage, executeDirectAction],
    );

    const handleCancel = useCallback(() => {
      if (streamSourceRef.current) {
        streamSourceRef.current.close();
        streamSourceRef.current = null;
      }
      setIsProcessing(false);
      setMessages((prev) => prev.map((msg) => ({ ...msg, isStreaming: false })));
    }, []);

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          bgcolor: '#ffffff',
          borderRadius: 2,
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
        }}
      >
        <ChatHeader messages={messages} onClearChat={handleClearChat} />

        {error && <ErrorMessage error={error} onClose={() => setError(null)} />}

        {/* Message Feed Area */}
        <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {messages.length === 0 ? (
            <Box
              sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 3,
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  bgcolor: '#eff6ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'primary.main',
                }}
              >
                <SmartToyIcon sx={{ fontSize: 26 }} />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', textAlign: 'center' }}>
                How can I help you today?
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 300 }}>
                Ask to discover data sources, create pipelines, execute steps, or search research papers.
              </Typography>
            </Box>
          ) : (
            <MessageFeed maxHeight="100%">
              {messages.map((msg) => (
                <MessageBubble
                  key={msg.id}
                  role={msg.role}
                  content={msg.content}
                  timestamp={msg.timestamp}
                  isStreaming={msg.isStreaming}
                  toolCalls={msg.toolCalls}
                  status={msg.status}
                >
                  {!msg.isStreaming &&
                    (msg.detectedEntities || []).map((detectedEntity, idx) => (
                      <Button
                        key={`${detectedEntity.type}_${detectedEntity.entityType || ''}_${detectedEntity.id || detectedEntity.name || idx}`}
                        size="small"
                        variant="outlined"
                        startIcon={<LayersIcon sx={{ fontSize: 14 }} />}
                        onClick={() => onEntityDetected && onEntityDetected(detectedEntity!)}
                        sx={{
                          fontSize: '0.72rem',
                          py: 0.4,
                          px: 1.25,
                          mt: 0.5,
                          textTransform: 'none',
                          fontWeight: 600,
                          borderColor: 'primary.light',
                          bgcolor: '#ffffff',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                          '&:hover': { bgcolor: '#f0fdf4', borderColor: 'primary.main' },
                        }}
                      >
                        {detectedEntity.type === 'entity_list'
                          ? `View ${detectedEntity.items?.length ?? ''} ${detectedEntity.title || 'Components'} in Workspace`
                          : detectedEntity.entityType === 'pipelineInstance'
                            ? `View Pipeline "${detectedEntity.name || detectedEntity.id}" in Workspace`
                            : detectedEntity.entityType === 'dataSourceInstance'
                              ? `View Data Source "${detectedEntity.name || detectedEntity.id}" in Workspace`
                              : detectedEntity.entityType === 'agentInstance'
                                ? `View Agent "${detectedEntity.name || detectedEntity.id}" in Workspace`
                                : `View User App "${detectedEntity.name || detectedEntity.id}" in Workspace`}
                      </Button>
                    ))}
                </MessageBubble>
              ))}
            </MessageFeed>
          )}
        </Box>

        {/* Chat Footer / Input */}
        <Box
          sx={{
            px: 1,
            pt: 0.5,
            pb: 0.5,
            borderTop: '1px solid #f1f5f9',
            bgcolor: '#fafafa',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          <QuickActionsBar
            quickActions={quickActions}
            isProcessing={isProcessing}
            activeActionLabel={activeActionLabel}
            onActionClick={handleQuickActionClick}
          />

          <MessageInput
            onSubmit={handleSendMessage}
            onCancel={handleCancel}
            disabled={isProcessing}
            placeholder="Ask anything about your data, agents, or pipelines..."
          />
        </Box>
      </Box>
    );
  },
);

export default AssistantChat;
